import { CheckIcon, XIcon } from "lucide-react";
import { computeHebrewDate } from "@triedge/domain-core";
import {
  useApproveDonationsDedicationMutation,
  useListDonationsPendingDedicationsQuery,
  useRejectDonationsDedicationMutation,
} from "../../../state/domainRouterApi";
import { ModuleSectionNav } from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { DONATIONS_SECTION_ITEMS } from "./sectionNav";

const PRIMARY = "var(--primary)";
const SUCCESS = "var(--aquamarine)";
const TEXT = "var(--iron-grey)";
const BORDER = "var(--border)";

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((index) => (
        <div key={index} className="h-16 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

type DedicationRow = {
  id?: string;
  date?: string;
  hebrewDate?: string;
  honoreeName?: string;
  dedicationType?: string;
  donorName?: string;
  donorEmail?: string;
};

function DedicationActionCell({ dedication }: { dedication: DedicationRow }) {
  const [approve, { isLoading: approving }] =
    useApproveDonationsDedicationMutation();
  const [reject, { isLoading: rejecting }] =
    useRejectDonationsDedicationMutation();

  const busy = approving || rejecting;

  const handleApprove = async () => {
    if (!dedication.id) return;
    try {
      await approve({ donationId: dedication.id }).unwrap();
    } catch {
      // Error is surfaced through RTK cache invalidation; parent re-fetches
    }
  };

  const handleReject = async () => {
    if (!dedication.id) return;
    try {
      await reject({ donationId: dedication.id }).unwrap();
    } catch {
      // Error is surfaced through RTK cache invalidation; parent re-fetches
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={handleApprove}
        className="yst-motion-color inline-flex items-center gap-1 rounded-md bg-jungle px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        aria-label={`Approve dedication for ${dedication.honoreeName ?? "unknown"}`}
      >
        <CheckIcon className="h-3 w-3" aria-hidden="true" />
        Approve
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={handleReject}
        className="yst-motion-color inline-flex items-center gap-1 rounded-md border border-coral bg-white px-3 py-1.5 text-xs font-semibold text-strawberry hover:bg-warning-background disabled:opacity-50"
        aria-label={`Reject dedication for ${dedication.honoreeName ?? "unknown"}`}
      >
        <XIcon className="h-3 w-3" aria-hidden="true" />
        Reject
      </button>
    </div>
  );
}

export function SchoolDonationsModerationPage() {
  const { data, isLoading, isError, error } =
    useListDonationsPendingDedicationsQuery({});

  const dedications: DedicationRow[] = (() => {
    if (!data) return [];
    const candidate = data as Record<string, unknown>;
    if (Array.isArray(candidate.items)) {
      return (candidate.items as Array<Record<string, unknown>>).map((item) => {
        const firstName =
          typeof item.donorFirstName === "string" ? item.donorFirstName : "";
        const lastName =
          typeof item.donorLastName === "string" ? item.donorLastName : "";
        const date =
          typeof item.targetGregorianDate === "string"
            ? item.targetGregorianDate
            : "";
        const donorName = Boolean(item.isAnonymous)
          ? "Anonymous"
          : typeof item.publicDisplayName === "string" && item.publicDisplayName
            ? item.publicDisplayName
            : [firstName, lastName].filter(Boolean).join(" ") || "Anonymous";
        return {
          id: typeof item.id === "string" ? item.id : undefined,
          date,
          hebrewDate: date
            ? computeHebrewDate(date, false).hebrewDateLabel
            : "--",
          honoreeName:
            typeof item.honoreeName === "string" ? item.honoreeName : "--",
          dedicationType:
            item.dedicationType === "MEMORY" ? "In Memory Of" : "In Honor Of",
          donorName,
        };
      });
    }
    return [];
  })();

  return (
    <main
      data-testid="school-donations-moderation"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <YstRouteTransition className="mx-auto max-w-[1440px] space-y-6">
        <div>
          <h2 className="yst-surface-title">Dedication Moderation</h2>
        </div>

        <ModuleSectionNav
          items={DONATIONS_SECTION_ITEMS}
          activeId="moderation"
          ariaLabel="Donations sections"
          desktopClassName="source-faithful-tabs"
        />

        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <ErrorBanner
            message={
              (error && typeof error === "object" && "message" in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              "Moderation data is not yet available. The donations backend has not been deployed."
            }
          />
        ) : dedications.length === 0 ? (
          <div
            className="rounded-xl border bg-white py-16 text-center text-sm font-medium shadow-sm"
            style={{ borderColor: BORDER, color: TEXT }}
          >
            No pending dedications
          </div>
        ) : (
          <div className="yst-surface-panel overflow-hidden">
            <div className="source-faithful-tableScroll">
              <table
                className="min-w-[860px] divide-y text-sm"
                style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
              >
                <thead>
                  <tr className="yst-surface-table-head">
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Date
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Hebrew Date
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Honoree
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Type
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Donor
                    </th>
                    <th
                      className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
                >
                  {dedications.map((dedication) => (
                    <tr
                      key={
                        dedication.id ??
                        `${dedication.date}-${dedication.honoreeName}`
                      }
                      className="yst-motion-color hover:bg-muted/70"
                    >
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {dedication.date ?? "--"}
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {dedication.hebrewDate ?? "--"}
                      </td>
                      <td
                        className="px-6 py-4 font-medium"
                        style={{ color: TEXT }}
                      >
                        {dedication.honoreeName ?? "--"}
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        <span
                          className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold"
                          style={{
                            backgroundColor: `${PRIMARY}18`,
                            color: PRIMARY,
                          }}
                        >
                          {dedication.dedicationType ?? "--"}
                        </span>
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {dedication.donorName ?? "Anonymous"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <DedicationActionCell dedication={dedication} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </YstRouteTransition>
    </main>
  );
}
